import { useMemo } from "react";
import { Box, Button, Collapse, Stack, Typography } from "@mui/material";
import ArrowRightIcon from "@mui/icons-material/ArrowRight";
import { FetcherBrandTraceRow, rowId } from "../helpers/fetcherCatalogue";

/** Which collection level a row came from, as the mockup labels it. */
type TraceLevel = "INTERFACE" | "REPORT" | "BRAND" | "RAW TABLE";

interface Props {
  /** one `section=connection` row */
  connection: FetcherBrandTraceRow;
  /** the `section=brand` row - the interface and brand heading the cascade */
  brand?: FetcherBrandTraceRow;
  /** every `section=object` row carrying this connection's config_id */
  objects: FetcherBrandTraceRow[];
  expanded: boolean;
  onToggle: () => void;
  onOpenConfig: () => void;
  onOpenReport: () => void;
}

/** Which `object_level` each box of the cascade counts. */
const LEVEL_OBJECT_LEVEL: { [key in TraceLevel]: string } = {
  INTERFACE: "interface_id",
  REPORT: "report_id",
  BRAND: "brand_id",
  "RAW TABLE": "raw_output_table_name",
};

/** The same four levels, read back from a shaping row's `object_level`. */
const OBJECT_LEVEL_LABEL: { [objectLevel: string]: TraceLevel } = {
  interface_id: "INTERFACE",
  report_id: "REPORT",
  brand_id: "BRAND",
  raw_output_table_name: "RAW TABLE",
};

/** A level the UI does not know is shown exactly as the query sent it. */
const objectLevelLabel = (objectLevel: string | null): string =>
  OBJECT_LEVEL_LABEL[objectLevel ?? ""] ?? objectLevel ?? "";

/** The mockup's .dot.if / .rp / .bd / .sd, one colour per collection level. */
const LEVEL_DOT: { [key in TraceLevel]: string } = {
  INTERFACE: "#5B54C9",
  REPORT: "#0E7C7B",
  BRAND: "#C2643B",
  "RAW TABLE": "#A9A7B4",
};

/** The mockup's .src.if / .rp / .bd - the badge on each shaping row. */
const LEVEL_BADGE: { [key in TraceLevel]: { bg: string; fg: string } } = {
  INTERFACE: { bg: "#EDE9F6", fg: "#5B54C9" },
  REPORT: { bg: "#E6F2F1", fg: "#0E7C7B" },
  BRAND: { bg: "#F8ECE4", fg: "#C2643B" },
  "RAW TABLE": { bg: "#F0F0F3", fg: "#6E6E78" },
};

const UNKNOWN_LEVEL_BADGE = { bg: "#F0F0F3", fg: "#6E6E78" };

const levelBadge = (objectLevel: string | null) => {
  const level = OBJECT_LEVEL_LABEL[objectLevel ?? ""];
  return level ? LEVEL_BADGE[level] : UNKNOWN_LEVEL_BADGE;
};

/**
 * The mockup's .pill - the connection's run state. The query derives
 * `connection_state` from want_run, status and the account's own active flag,
 * so the text is shown as delivered.
 */
const StatePill = ({ state }: { state: string }) => {
  const isActive = state.toLowerCase() === "active";
  return (
    <Box
      component="span"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: "5px",
        padding: "2px 9px",
        borderRadius: "10px",
        fontSize: "11px",
        fontWeight: 500,
        whiteSpace: "nowrap",
        ...(isActive
          ? { backgroundColor: "#E7F5EC", color: "#1E8E4E" }
          : { backgroundColor: "#F0F0F3", color: "#6E6E78" }),
      }}
    >
      {isActive ? `✓ ${state}` : state}
    </Box>
  );
};

/** One of the four levels feeding the cascade (the mockup's .lvl). */
const LevelBox = ({
  level,
  name,
  count,
}: {
  level: TraceLevel;
  name: string;
  count: number;
}) => (
  <Box
    sx={{
      border: "1px solid #E7E7EC",
      borderStyle: count === 0 ? "dashed" : "solid",
      backgroundColor: count === 0 ? "#FCFCFD" : "#FFFFFF",
      borderRadius: "8px",
      padding: "6px 11px",
      minWidth: 0,
    }}
  >
    <Stack direction="row" alignItems="center" spacing={0.75}>
      <Box
        sx={{
          width: 8,
          height: 8,
          borderRadius: "2px",
          flex: "none",
          backgroundColor: LEVEL_DOT[level],
        }}
      />
      <Typography
        sx={{
          fontSize: "10px",
          letterSpacing: "0.6px",
          textTransform: "uppercase",
          fontWeight: 700,
          color: "#6E6E78",
        }}
      >
        {level}
      </Typography>
    </Stack>
    <Stack
      direction="row"
      alignItems="baseline"
      spacing={0.875}
      sx={{ mt: "3px" }}
    >
      <Typography
        sx={{
          fontSize: "13px",
          fontWeight: 500,
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        {name}
      </Typography>
      <Box
        component="span"
        sx={{
          fontSize: "11px",
          fontWeight: 600,
          color: count === 0 ? "#9A9AA4" : "#2A2A30",
        }}
      >
        {count}
      </Box>
    </Stack>
  </Box>
);

const Plus = () => (
  <Box
    sx={{
      display: "grid",
      placeItems: "center",
      color: "#9A9AA4",
      padding: "0 9px",
      fontSize: "13px",
    }}
  >
    +
  </Box>
);

/**
 * One brand-to-config connection: what each of the four levels contributes,
 * what the collector ends up sending, and the rows that shape the request.
 */
const BrandTraceConnectionCard = ({
  connection,
  brand,
  objects,
  expanded,
  onToggle,
  onOpenConfig,
  onOpenReport,
}: Props) => {
  const counts = useMemo(() => {
    const byLevel: { [objectLevel: string]: number } = {};
    let effective = 0;
    let overridden = 0;
    objects.forEach((row) => {
      const level = row.object_level ?? "";
      byLevel[level] = (byLevel[level] ?? 0) + 1;
      // Number(null) is 0, so a missing flag is neither sent nor dropped
      if (row.is_effective === null || row.is_effective === undefined) return;
      if (Number(row.is_effective) === 1) effective += 1;
      else if (Number(row.is_effective) === 0) overridden += 1;
    });
    return { byLevel, effective, overridden };
  }, [objects]);

  const levelCount = (level: TraceLevel) =>
    counts.byLevel[LEVEL_OBJECT_LEVEL[level]] ?? 0;

  return (
    <Box sx={{ borderTop: "1px solid #E7E7EC" }}>
      <Stack
        direction="row"
        alignItems="center"
        spacing={1.375}
        onClick={onToggle}
        data-tracker="1139"
        sx={{
          padding: "12px 18px",
          cursor: "pointer",
          backgroundColor: expanded ? "#F8F2FB" : "transparent",
          "&:hover": { backgroundColor: expanded ? "#F8F2FB" : "#F7F5FA" },
        }}
      >
        <ArrowRightIcon
          sx={{
            fontSize: "18px",
            color: "#9A9AA4",
            flex: "none",
            transition: "transform 0.15s",
            transform: expanded ? "rotate(90deg)" : "none",
          }}
        />
        <Typography sx={{ fontSize: "13.5px", fontWeight: 500 }}>
          {connection.config_name}{" "}
          <Box component="span" sx={{ color: "#9A9AA4", fontSize: "0.88em" }}>
            ({rowId(connection.config_id)})
          </Box>
        </Typography>
        {connection.connection_state && (
          <StatePill state={connection.connection_state} />
        )}
        <Typography
          sx={{ fontFamily: "monospace", fontSize: "11px", color: "#9A9AA4" }}
        >
          {connection.raw_table}
        </Typography>
        <Box sx={{ flex: 1 }} />
        <Typography sx={{ fontSize: "12.5px", color: "#6E6E78" }}>
          {counts.effective} objects
          <Box component="span" sx={{ color: "#9A9AA4", mx: "7px" }}>
            |
          </Box>
          report: {connection.report_name}
        </Typography>
      </Stack>

      <Collapse in={expanded} unmountOnExit>
        <Box sx={{ padding: "4px 18px 18px 40px", backgroundColor: "#FCFCFD" }}>
          <Stack
            direction="row"
            alignItems="flex-start"
            spacing={2.5}
            sx={{ mt: 1.25 }}
          >
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Stack direction="row" alignItems="stretch" flexWrap="wrap">
                <LevelBox
                  level="INTERFACE"
                  name={brand?.interface_name ?? ""}
                  count={levelCount("INTERFACE")}
                />
                <Plus />
                <LevelBox
                  level="REPORT"
                  name={connection.report_name ?? ""}
                  count={levelCount("REPORT")}
                />
                <Plus />
                <LevelBox
                  level="BRAND"
                  name={brand?.brand_name ?? ""}
                  count={levelCount("BRAND")}
                />
                <Plus />
                <LevelBox
                  level="RAW TABLE"
                  name={connection.raw_table ?? ""}
                  count={levelCount("RAW TABLE")}
                />

                <Box
                  sx={{
                    display: "grid",
                    placeItems: "center",
                    pl: 1.5,
                    pr: 0.5,
                  }}
                >
                  <Box
                    sx={{
                      borderRadius: "8px",
                      padding: "6px 13px 7px",
                      backgroundColor: "#F8F2FB",
                      border: "1px solid #E4D3EC",
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: "10px",
                        letterSpacing: "0.6px",
                        textTransform: "uppercase",
                        fontWeight: 700,
                        color: "secondary.main",
                      }}
                    >
                      Effective
                    </Typography>
                    <Stack
                      direction="row"
                      alignItems="baseline"
                      spacing={0.875}
                    >
                      <Typography sx={{ fontSize: "13px", fontWeight: 500 }}>
                        {counts.effective}
                      </Typography>
                      <Typography sx={{ fontSize: "11px", color: "#9A9AA4" }}>
                        {counts.overridden} overridden
                      </Typography>
                    </Stack>
                  </Box>
                </Box>
              </Stack>

              <Typography sx={{ fontSize: "11.5px", color: "#9A9AA4", mt: 1 }}>
                More specific levels win on the same key. <b>url_param</b> and{" "}
                <b>ignored_field_on_compare</b> accumulate instead of
                overriding.
              </Typography>

              <Typography
                sx={{ fontSize: "12.5px", color: "#6E6E78", mt: 1.125 }}
              >
                Recipe:{" "}
                <Box
                  component="span"
                  sx={{ fontFamily: "monospace", fontSize: "12px" }}
                >
                  {connection.recipe}
                </Box>
              </Typography>
            </Box>

            <Box
              sx={{
                flex: "none",
                textAlign: "right",
                fontSize: "12.5px",
                color: "#6E6E78",
                whiteSpace: "nowrap",
                pt: "2px",
              }}
            >
              Writes to{" "}
              <Box component="span" sx={{ fontFamily: "monospace" }}>
                {connection.raw_table}
              </Box>
              <Stack
                direction="row"
                spacing={0.75}
                justifyContent="flex-end"
                sx={{ mt: "5px" }}
              >
                <Button
                  //underline="always"
                  //color="secondary"

                  sx={{ fontSize: "12.5px" }}
                  onClick={onOpenConfig}
                  disabled
                >
                  edit config
                </Button>
                <Box component="span" sx={{ color: "#9A9AA4" }}>
                  ·
                </Box>
                <Button
                  component="button"
                  // underline="always"
                  //color="secondary"

                  sx={{ fontSize: "12.5px" }}
                  onClick={onOpenReport}
                  disabled
                >
                  edit report
                </Button>
              </Stack>
            </Box>
          </Stack>

          <Box
            sx={{
              border: "1px solid #E7E7EC",
              borderRadius: "9px",
              mt: 1.25,
              overflow: "hidden",
              backgroundColor: "#FFFFFF",
            }}
          >
            <Stack
              direction="row"
              alignItems="center"
              spacing={1.125}
              sx={{
                padding: "9px 13px",
                backgroundColor: "#FAFAFC",
                borderBottom: "1px solid #E7E7EC",
              }}
            >
              <Typography
                sx={{
                  fontSize: "11.5px",
                  fontWeight: 700,
                  letterSpacing: "0.4px",
                  textTransform: "uppercase",
                  color: "#9A9AA4",
                }}
              >
                Request shaping
              </Typography>
              <Typography sx={{ fontSize: "11.5px", color: "#9A9AA4" }}>
                {objects.length}
              </Typography>
            </Stack>

            {objects.length === 0 ? (
              <Typography
                sx={{
                  padding: "16px 14px",
                  fontSize: "12.5px",
                  color: "#6E6E78",
                  lineHeight: 1.6,
                  backgroundColor: "#FAFAFC",
                }}
              >
                No objects shape this request - the collector builds the call in
                code for this interface.
              </Typography>
            ) : (
              objects.map((row, index) => (
                <Stack
                  key={`${rowId(row.object_id)}-${index}`}
                  direction="row"
                  spacing={1.5}
                  alignItems="flex-start"
                  sx={{
                    padding: "8px 13px",
                    borderBottom:
                      index === objects.length - 1
                        ? "none"
                        : "1px solid #F0F0F3",
                    fontSize: "12.5px",
                  }}
                >
                  <Box sx={{ width: 190, flex: "none" }}>
                    <Typography
                      sx={{
                        fontFamily: "monospace",
                        fontSize: "12px",
                        fontWeight: 500,
                      }}
                    >
                      {row.field_type}
                    </Typography>
                    {Number(row.is_union_key) === 1 && (
                      <Box
                        component="span"
                        sx={{
                          display: "inline-flex",
                          backgroundColor: "#EDE9F6",
                          color: "#5B54C9",
                          borderRadius: "3px",
                          padding: "0 5px",
                          fontSize: "9.5px",
                          fontWeight: 700,
                          letterSpacing: "0.3px",
                          mt: "2px",
                        }}
                      >
                        UNION
                      </Box>
                    )}
                  </Box>

                  <Typography
                    sx={{
                      flex: 1,
                      minWidth: 0,
                      fontFamily: "monospace",
                      fontSize: "12px",
                      color: "#6E6E78",
                      overflowX: "auto",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {row.field_value}
                  </Typography>

                  <Box
                    component="span"
                    sx={{
                      flex: "none",
                      display: "inline-flex",
                      alignItems: "center",
                      padding: "1px 7px",
                      borderRadius: "4px",
                      fontSize: "10px",
                      fontWeight: 700,
                      letterSpacing: "0.3px",
                      backgroundColor: levelBadge(row.object_level).bg,
                      color: levelBadge(row.object_level).fg,
                    }}
                  >
                    {objectLevelLabel(row.object_level)}
                  </Box>
                </Stack>
              ))
            )}
          </Box>
        </Box>
      </Collapse>
    </Box>
  );
};

export default BrandTraceConnectionCard;
